/**
 * SearchableSelect — a drop-in replacement for Meridian's `Select` that makes
 * the dropdown type-ahead searchable.
 *
 * Meridian's `Select` becomes searchable when you pass `onSearch` (renders the
 * search input) + `searchQuery` (controlled query), but it leaves option
 * filtering to the app. This wrapper owns the query state and filters its
 * `SelectOption` children by their `label` (and `value`), so every call site
 * gets a searchable dropdown for free without repeating the boilerplate.
 *
 * Search auto-enables only when there are more than `searchThreshold` options
 * (default 6), so short fixed enums (Currency, Payment Terms, status filters)
 * stay clean while long data-backed lists (suppliers, items, recipes,
 * locations, stores) become searchable. Pass `searchable` to force it on/off.
 *
 * Direct Meridian import (no barrel) per the Portal Meridian rules; `SelectOption`
 * is the allowed sub-export.
 */
import React, { Children, isValidElement, useMemo, useState } from "react"
import Select from "@amzn/meridian/select"

type SelectProps = React.ComponentProps<typeof Select>

export interface SearchableSelectProps extends SelectProps {
  /** Force search on/off. Default: auto — on when option count > searchThreshold. */
  searchable?: boolean
  /** Option-count threshold above which search auto-enables (default 6). */
  searchThreshold?: number
}

interface OptionLikeProps {
  label?: string
  value?: unknown
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  children,
  searchable,
  searchThreshold = 6,
  ...selectProps
}) => {
  const [query, setQuery] = useState("")

  const childArray = useMemo(() => Children.toArray(children), [children])
  const enableSearch = searchable ?? childArray.length > searchThreshold

  const filteredChildren = useMemo(() => {
    if (!enableSearch || !query.trim()) {
      return children
    }
    const needle = query.trim().toLowerCase()
    return childArray.filter((child) => {
      // Non-element children (e.g. group wrappers) are kept rather than hidden.
      if (!isValidElement(child)) {
        return true
      }
      const { label, value } = (child.props ?? {}) as OptionLikeProps
      const haystack = `${label ?? ""} ${value ?? ""}`.toLowerCase()
      return haystack.includes(needle)
    })
  }, [children, childArray, enableSearch, query])

  // Closed-state label. Meridian's DEFAULT valueLabel derives the field text
  // from the selected option among the CURRENTLY RENDERED children; once the
  // search filter removes the selected option from the children, the default
  // resolves to "" and the field shows the placeholder (the selection looks
  // like it didn't take). Per Meridian's guidance for a filtered Select, supply
  // a valueLabel that resolves from the FULL (unfiltered) child list so the
  // selected label always shows. Skip it for multi-select (array value), where
  // Meridian's "N selected" default is what we want.
  const selectedLabel = useMemo(() => {
    if (Array.isArray(selectProps.value)) {
      return undefined
    }
    const match = childArray.find(
      (child) => isValidElement(child) && (child.props as OptionLikeProps).value === selectProps.value,
    )
    return isValidElement(match) ? ((match.props as OptionLikeProps).label ?? "") : ""
  }, [childArray, selectProps.value])

  const handleChange = (next: unknown) => {
    selectProps.onChange?.(next)
    // Clear the filter so the field shows the selected label and the next open
    // lists everything.
    setQuery("")
  }

  return (
    <Select
      {...selectProps}
      onChange={handleChange}
      searchQuery={enableSearch ? query : undefined}
      onSearch={enableSearch ? setQuery : undefined}
      {...(selectedLabel !== undefined ? { valueLabel: () => selectedLabel } : {})}
    >
      {filteredChildren}
    </Select>
  )
}

export default SearchableSelect
